import { zxcvbn, zxcvbnOptions } from "@zxcvbn-ts/core";
import {
	adjacencyGraphs,
	dictionary as commonDictionary,
} from "@zxcvbn-ts/language-common";
import { dictionary, translations } from "@zxcvbn-ts/language-en";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
} from "@/components/primitives";
import { useAuth } from "@/contexts/AuthContext";
import { TermsDialog } from "@/features/terms/TermsDialog";
import { supabase } from "@/lib/supabase";

const BACKGROUND_IMAGES = [
	"david-billings-__I-gX-cnd8-unsplash (1).jpg",
	"dvir-adler-uSj_ce8vtyE-unsplash.jpg",
	"getty-images-kjXpmeqUYsU-unsplash.jpg",
	"joshua-earle-4UexhWLVcnY-unsplash.jpg",
	"joshua-earle-Ld583Sij9-E-unsplash.jpg",
	"joshua-earle-oz1GKlRWab4-unsplash.jpg",
	"joshua-earle-PUHfSAQtJeo-unsplash.jpg",
	"joshua-earle-zYT8Ih3iPGA-unsplash (1).jpg",
	"marc-najera-JWAAgQSbq44-unsplash.jpg",
	"patrick-tomasso-ekIyE1QXyL4-unsplash.jpg",
	"pine-watt-2Hzmz15wGik-unsplash.jpg",
	"tegan-mierle-ioyEITUD2G8-unsplash.jpg",
	"xan-griffin-eA2t5EvcxU4-unsplash.jpg",
	"jeffrey-f-lin-oc8m9Cj8dsc-unsplash.jpg",
	"hudson-hintze-vpxeE7s-my4-unsplash.jpg",
	"helena-cook-5ed6i2hXeZk-unsplash.jpg",
	"getty-images-Zp1ZAw0tusg-unsplash.jpg",
	"getty-images-aVRkLOFVNK4-unsplash.jpg",
];

function getRandomBackground() {
	const randomIndex = Math.floor(Math.random() * BACKGROUND_IMAGES.length);
	console.log("randomIndex " + randomIndex);
	return `/img/random-backgrounds/${BACKGROUND_IMAGES[randomIndex]}`;
}

const BUILD_COMMIT: string = import.meta.env.VITE_BUILD_COMMIT || "dev";
const BUILD_TIME: string = __BUILD_TIME__ || "";

export function LoginPage() {
	const { signIn, signUp, user, profile, loading: authLoading } = useAuth();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [loadingSignIn, setLoadingSignIn] = useState(false);
	const [loadingSignUp, setLoadingSignUp] = useState(false);
	const [error, setError] = useState("");
	const [showEmailVerification, setShowEmailVerification] = useState(false);
	const [signupEmail, setSignupEmail] = useState("");
	const [justSignedUp, setJustSignedUp] = useState(false);
	const [showTerms, setShowTerms] = useState(false);
	const [backgroundImage, setBackgroundImage] = useState("");
	const [signupPassword, setSignupPassword] = useState("");
	const [signupConfirm, setSignupConfirm] = useState("");
	const [passwordScore, setPasswordScore] = useState(0);
	const [showSignInPassword, setShowSignInPassword] = useState(false);
	const [showSignupPassword, setShowSignupPassword] = useState(false);
	const [showSignupConfirm, setShowSignupConfirm] = useState(false);
	const [showResetDialog, setShowResetDialog] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [resetLoading, setResetLoading] = useState(false);
	const [resetError, setResetError] = useState("");
	const [resetSent, setResetSent] = useState(false);

	useEffect(() => {
		setBackgroundImage(getRandomBackground());
	}, []);

	// Redirect mentors who sign in on the mentee login page
	useEffect(() => {
		if (!authLoading && user && profile && profile.user_type === "mentor") {
			console.log(
				"LoginPage: Detected mentor on mentee login page, redirecting to mentor login",
			);
			navigate("/mentor-login?redirected=true", { replace: true });
		}
	}, [authLoading, user, profile, navigate]);

	// Configure zxcvbn (common + English)
	zxcvbnOptions.setOptions({
		translations,
		graphs: adjacencyGraphs,
		dictionary: { ...commonDictionary, ...dictionary },
	});

	const strengthMeta = (score: number) => {
		switch (score) {
			case 0:
				return { label: "Very weak", color: "bg-red-500", bar: "w-1/5" };
			case 1:
				return { label: "Weak", color: "bg-orange-500", bar: "w-2/5" };
			case 2:
				return { label: "Fair", color: "bg-yellow-500", bar: "w-3/5" };
			case 3:
				return { label: "Good", color: "bg-green-500", bar: "w-4/5" };
			default:
				return { label: "Strong", color: "bg-emerald-600", bar: "w-full" };
		}
	};

	const [passwordFeedback, setPasswordFeedback] = useState<string | null>(null);
	const [pwnedCount, setPwnedCount] = useState<number | null>(null);

	useEffect(() => {
		if (!signupPassword) {
			setPasswordScore(0);
			setPasswordFeedback(null);
			setPwnedCount(null);
			return;
		}
		const result = zxcvbn(signupPassword);
		setPasswordScore(result.score);
		const fb =
			result.feedback?.warning || result.feedback?.suggestions?.[0] || null;
		setPasswordFeedback(fb || null);
	}, [signupPassword]);

	// HIBP (k-anonymity) check
	async function sha1Hex(input: string): Promise<string> {
		const data = new TextEncoder().encode(input);
		const hash = await crypto.subtle.digest("SHA-1", data);
		return Array.from(new Uint8Array(hash))
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("")
			.toUpperCase();
	}

	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (!signupPassword) {
				setPwnedCount(null);
				return;
			}
			try {
				const full = await sha1Hex(signupPassword);
				const prefix = full.slice(0, 5);
				const suffix = full.slice(5);
				const res = await fetch(
					`https://api.pwnedpasswords.com/range/${prefix}`,
				);
				const text = await res.text();
				let count = 0;
				for (const line of text.split("\n")) {
					const [suf, cnt] = line.trim().split(":");
					if (suf === suffix) {
						count = parseInt(cnt || "0", 10) || 0;
						break;
					}
				}
				if (!cancelled) setPwnedCount(count);
			} catch {
				if (!cancelled) setPwnedCount(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [signupPassword]);

	// Build a safe redirect target
	const rawRedirect = searchParams.get("redirect") || "/";
	const safeRedirect = (() => {
		try {
			// Only allow same-origin, absolute app paths
			if (rawRedirect.startsWith("/")) {
				// Prevent redirect loop back to login
				if (rawRedirect.startsWith("/login")) return "/";
				return rawRedirect;
			}
			return "/";
		} catch {
			return "/";
		}
	})();

	// Redirect authenticated users
	// If user is a mentor, redirect them to mentor login page
	useEffect(() => {
		if (!authLoading && user && profile) {
			if (profile.user_type === "mentor") {
				console.log("LoginPage: User is a mentor, redirecting to mentor login");
				navigate("/mentor-login", { replace: true });
			} else {
				console.log("LoginPage: User is authenticated, redirecting...");
				console.log("LoginPage: Redirecting to:", safeRedirect);
				navigate(safeRedirect, { replace: true });
			}
		}
	}, [authLoading, user, profile, navigate, safeRedirect]);

	const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setLoadingSignIn(true);
		setError("");

		const formData = new FormData(e.currentTarget);
		const email = formData.get("email") as string;
		const password = formData.get("password") as string;

		const { error } = await signIn(email, password);

		if (error) {
			setError(error.message);
			// If this is the user who just signed up and got an email verification error,
			// suggest they check their email
			if (
				justSignedUp &&
				email === signupEmail &&
				(error.message?.includes("Email not confirmed") ||
					error.message?.includes("verification") ||
					error.message?.includes("Invalid email or password"))
			) {
				setError(
					"Please check your email and click the verification link before signing in. It may take a few minutes to arrive.",
				);
			}
		}

		setLoadingSignIn(false);
	};

	const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setLoadingSignUp(true);
		setError("");

		const formData = new FormData(e.currentTarget);
		const name = formData.get("name") as string;
		const email = formData.get("email") as string;
		const password = formData.get("password") as string;
		const confirm = formData.get("confirm_password") as string;
		// Mentor registration is disabled - force mentee type
		const userType = "mentee" as const;

		// Clear password fields immediately for security
		setSignupPassword("");
		setSignupConfirm("");

		if (password !== confirm) {
			setError("Passwords do not match.");
			setLoadingSignUp(false);
			return;
		}
		if (password.length < 8) {
			setError("Password must be at least 8 characters long.");
			setLoadingSignUp(false);
			return;
		}

		console.log("Starting signup process for:", email);
		const { error } = await signUp(email, password, name, userType);

		if (error) {
			console.error("Signup error:", error);
			setError(error.message);
		} else {
			console.log("Signup successful, showing email verification dialog");
			setSignupEmail(email);
			setShowEmailVerification(true);
			setJustSignedUp(true);
			// Clear the form
			e.currentTarget.reset();
			setSignupPassword("");
			setSignupConfirm("");
		}

		setLoadingSignUp(false);
	};

	const [showPasswordResetComplete, setShowPasswordResetComplete] =
		useState(false);
	const [showSetNewPassword, setShowSetNewPassword] = useState(false);
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [setPwLoading, setSetPwLoading] = useState(false);
	const [setPwError, setSetPwError] = useState("");

	useEffect(() => {
		// Detect Supabase recovery redirect (hash or query)
		const search = new URLSearchParams(window.location.search);
		const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
		const isRecovery =
			search.get("type") === "recovery" || hash.get("type") === "recovery";
		if (isRecovery) {
			setShowSetNewPassword(true);
			// Clean URL to avoid leaking tokens in UI/history
			try {
				const cleanUrl = window.location.origin + window.location.pathname;
				window.history.replaceState({}, document.title, cleanUrl);
			} catch {}
		}
	}, []);

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
			{/* Left Panel - Background */}
			<div
				className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative bg-cover bg-center bg-no-repeat"
				style={{
					backgroundImage: `url('${backgroundImage}')`,
				}}
			></div>

			{/* Right Panel - Forms */}
			<div className="w-full lg:w-1/2 flex flex-col justify-between p-8">
				<div className="flex-1 flex items-center justify-center">
					<div className="w-full max-w-md">
						<div className="text-center mb-8">
							<h1 className="text-3xl lg:text-4xl font-bold text-primary lg:text-gray-900 dark:lg:text-white">
								Welcome to your Reflection Journal
							</h1>
							<p className="text-gray-600 dark:text-gray-400 mt-2 lg:text-lg lg:opacity-90">
								Life can sometimes feel overwhelming, and understanding what
								causes us stress is the first step to managing it.
							</p>
						</div>

						<Card>
							<CardHeader>
								<CardTitle>Sign In</CardTitle>
								<CardDescription>
									Enter your credentials to access your account
								</CardDescription>
							</CardHeader>
							<CardContent>
								{justSignedUp && (
									<div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
										<p className="text-sm text-blue-800">
											<strong>Welcome!</strong> Please check your email and
											click the verification link, then sign in below.
										</p>
									</div>
								)}
								<form onSubmit={handleSignIn} className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="signin-email">Email</Label>
										<Input
											id="signin-email"
											name="email"
											type="email"
											autoComplete="username"
											placeholder="Enter your email"
											defaultValue={justSignedUp ? signupEmail : ""}
											required
											data-testid="login-email-input"
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="signin-password">Password</Label>
										<div className="relative">
											<Input
												id="signin-password"
												name="password"
												type={showSignInPassword ? "text" : "password"}
												autoComplete="current-password"
												placeholder="Enter your password"
												required
												className="pr-10"
												data-testid="login-password-input"
											/>
											<button
												type="button"
												aria-label="Toggle password visibility"
												className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
												onClick={() => setShowSignInPassword((v) => !v)}
											>
												{showSignInPassword ? (
													<EyeOff className="w-4 h-4" />
												) : (
													<Eye className="w-4 h-4" />
												)}
											</button>
										</div>
									</div>
									<div className="flex items-center justify-between">
										{error ? (
											<div
												data-testid="login-error-message"
												className="text-red-600 text-sm"
											>
												{error}
											</div>
										) : (
											<div />
										)}
										<button
											type="button"
											className="text-sm text-primary hover:underline"
											data-testid="password-reset-link"
											onClick={() => {
												const emailInput =
													(
														document.getElementById(
															"signin-email",
														) as HTMLInputElement | null
													)?.value || "";
												setResetEmail(emailInput);
												setResetError("");
												setResetSent(false);
												setShowResetDialog(true);
											}}
										>
											Forgot password?
										</button>
									</div>
									<Button
										type="submit"
										className="w-full mt-2"
										disabled={loadingSignIn}
										data-testid="login-submit-button"
									>
										{loadingSignIn ? "Signing In..." : "Sign In"}
									</Button>
								</form>
							</CardContent>
						</Card>
					</div>
				</div>

				{/* Footer */}
				<div className="flex flex-col items-center justify-center mt-8 pt-4">
					<Link
						to="/mentor-login"
						className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary mb-3 underline"
						data-testid="mentor-login-link"
					>
						Mentor Login
					</Link>
					<img
						src="/img/clarity-insights-logo.png"
						alt="Clarity Insights Logo"
						className="h-12 mb-2 opacity-60"
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						Copyright{" "}
						<a href="https://www.clarityinsightsgroup.com">
							Clarity Insights Group Ltd
						</a>{" "}
						2025
					</p>
					<button
						type="button"
						onClick={() => setShowTerms(true)}
						className="mt-2 text-xs text-primary underline hover:opacity-80"
						aria-label="View Terms and Conditions"
					>
						View Terms & Conditions
					</button>
					<div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
						Build {BUILD_COMMIT} • {new Date(BUILD_TIME).toLocaleString()}
					</div>
				</div>
			</div>

			{/* Password Reset Dialog */}
			<Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Reset your password</DialogTitle>
						<DialogDescription>
							Enter your account email and we'll send you a password reset link.
						</DialogDescription>
					</DialogHeader>
					<form
						onSubmit={async (e) => {
							e.preventDefault();
							setResetLoading(true);
							setResetError("");
							setResetSent(false);
							try {
								const email = resetEmail.trim();
								if (!email) {
									setResetError("Please enter your email address.");
									setResetLoading(false);
									return;
								}
								const { error } = await supabase.auth.resetPasswordForEmail(
									email,
									{
										redirectTo: `${window.location.origin}/auth/reset-password`,
									},
								);
								if (error) {
									setResetError(error.message || "Failed to send reset email.");
								} else {
									setResetSent(true);
								}
							} finally {
								setResetLoading(false);
							}
						}}
						className="space-y-3"
					>
						<div className="space-y-2">
							<Label htmlFor="reset-email">Email</Label>
							<Input
								id="reset-email"
								type="email"
								autoComplete="email"
								value={resetEmail}
								onChange={(e) => setResetEmail(e.target.value)}
								placeholder="you@example.com"
								required
							/>
						</div>
						{resetError && (
							<div className="text-red-600 text-sm">{resetError}</div>
						)}
						{resetSent && (
							<div className="text-green-600 text-sm">
								If an account exists for this email, a reset link has been sent.
							</div>
						)}
						<div className="flex justify-end gap-2 pt-2">
							{resetSent ? (
								<Button type="button" onClick={() => setShowResetDialog(false)}>
									OK
								</Button>
							) : (
								<>
									<Button
										type="button"
										variant="ghost"
										onClick={() => setShowResetDialog(false)}
									>
										Cancel
									</Button>
									<Button type="submit" disabled={resetLoading}>
										{resetLoading ? "Sending..." : "Send reset link"}
									</Button>
								</>
							)}
						</div>
					</form>
				</DialogContent>
			</Dialog>

			{/* Set New Password (after recovery link) */}
			<Dialog open={showSetNewPassword} onOpenChange={setShowSetNewPassword}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Set a new password</DialogTitle>
						<DialogDescription>
							Enter and confirm your new password to complete the reset.
						</DialogDescription>
					</DialogHeader>
					<form
						onSubmit={async (e) => {
							e.preventDefault();
							setSetPwError("");
							if (newPassword.length < 8) {
								setSetPwError("Password must be at least 8 characters long.");
								return;
							}
							if (newPassword !== confirmNewPassword) {
								setSetPwError("Passwords do not match.");
								return;
							}
							setSetPwLoading(true);
							try {
								const { error } = await supabase.auth.updateUser({
									password: newPassword,
								});
								if (error) {
									setSetPwError(error.message || "Failed to update password.");
								} else {
									setShowSetNewPassword(false);
									setShowPasswordResetComplete(true);
									setNewPassword("");
									setConfirmNewPassword("");
								}
							} finally {
								setSetPwLoading(false);
							}
						}}
						className="space-y-3"
					>
						<div className="space-y-2">
							<Label htmlFor="new-password">New password</Label>
							<Input
								id="new-password"
								type="password"
								autoComplete="new-password"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								placeholder="Enter a new password"
								required
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirm-new-password">Confirm new password</Label>
							<Input
								id="confirm-new-password"
								type="password"
								autoComplete="new-password"
								value={confirmNewPassword}
								onChange={(e) => setConfirmNewPassword(e.target.value)}
								placeholder="Re-enter your new password"
								required
							/>
						</div>
						{setPwError && (
							<div className="text-red-600 text-sm">{setPwError}</div>
						)}
						<div className="flex justify-end gap-2 pt-2">
							<Button
								type="button"
								variant="ghost"
								onClick={() => setShowSetNewPassword(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={setPwLoading}>
								{setPwLoading ? "Saving..." : "Save new password"}
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>

			{/* Password reset completed notice */}
			<Dialog
				open={showPasswordResetComplete}
				onOpenChange={setShowPasswordResetComplete}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Password updated</DialogTitle>
						<DialogDescription>
							Your password has been updated. You can now sign in with your new
							password.
						</DialogDescription>
					</DialogHeader>
					<div className="flex justify-end">
						<Button onClick={() => setShowPasswordResetComplete(false)}>
							OK
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Email Verification Dialog */}
			<Dialog
				open={showEmailVerification}
				onOpenChange={setShowEmailVerification}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
								<svg
									className="w-5 h-5 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							Account Created Successfully!
						</DialogTitle>
						<DialogDescription>
							We've sent a verification email to <strong>{signupEmail}</strong>.
							Please check your email and click the verification link to
							activate your account.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
							<strong>Next steps:</strong>
							<ul className="list-disc list-inside mt-1 space-y-1">
								<li>Check your email inbox (and spam folder)</li>
								<li>Click the verification link in the email</li>
								<li>Return here to sign in with your credentials</li>
							</ul>
						</div>
					</div>
					<div className="flex justify-end">
						<Button
							onClick={() => {
								setShowEmailVerification(false);
								setLoadingSignIn(false);
								setLoadingSignUp(false);
							}}
						>
							Got it!
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			{/* T&Cs Viewer */}
			<TermsDialog open={showTerms} onOpenChange={setShowTerms} mode="view" />
		</div>
	);
}
